import Word from "./Word.js";

/**
 * Class representing a word cloud. It manages the layout and animation of words within a container.
 * Starts with an elliptical layout and a explosion effect.
 * @class WordCloud
 */

//Disable eslint rule for class methods that are not used yet

export default class WordCloud {
	/**
	 * Array of Word objects in the cloud.
	 * @type {Word[]}
	 */
	words = [];

	/**
	 * The container element for the word cloud.
	 * @type {HTMLElement}
	 */
	element = null;

	/**
	 * Size of the word cloud container.
	 * @type {Object}
	 */
	size = { width: 0, height: 0 };

	/**
	 * Center point of the word cloud container.
	 * @type {Object}
	 */
	center = { x: 0, y: 0 };

	/**
	 * Current mouse position.
	 * @type {Object}
	 */
	mousePosition = { x: 0, y: 0 };

	/**
	 * @type {Object|null} logoArea - Position and size information for the logo area
	 */
	logoArea = null;

	/**
	 * Constant for ellipse major axis.
	 */
	static ELLIPSE_A = 600;

	/**
	 * Constant for ellipse minor axis.
	 */
	static ELLIPSE_B = 400;

	/**
	 * Basic padding value used in layout calculations.
	 * @type {number}
	 */
	static BASIC_PADDING = 24;

	/**
	 * Range for randomness in word placement.
	 * @type {number}
	 */
	static RANDOMNESS_RANGE = 200;

	/**
	 * Offset for randomness calculation.
	 * @type {number}
	 */
	static RANDOMNESS_OFFSET = 100;

	/**
	 * Creates an instance of WordCloud.
	 * @constructor
	 * @param {HTMLElement} element - The container element for the word cloud.
	 */
	constructor(element) {
		this.element = element;
		this.size.width = this.element.offsetWidth;
		this.size.height = this.element.offsetHeight;
		this.#setLogoArea();

		this.center = {
			x: this.size.width / 2,
			y: this.size.height / 2,
		};
		this.#createWords();
		this.#registerEventListeners();
	}

	/**
	 * Creates Word objects for each word element in the cloud.
	 * @private
	 * @returns {void}
	 */
	#createWords() {
		const wordElements = this.element.querySelectorAll(
			`#${this.element.id} .word-cloud__word`,
		);
		wordElements.forEach((wordElement) => {
			const word = new Word(wordElement);
			this.words.push(word);
		});
		this.#setPoints(
			this.center,
			{
				a: Math.max(this.size.width / 3, WordCloud.ELLIPSE_A),
				b: this.size.height / 4,
			},
			this.words,
		);
	}

	/**
	 * Calculates positions for words in an elliptical layout with randomness.
	 *
	 * @param {Object} center - center point with x and y coordinates
	 * @param {Object} radius - radius with a and b values for ellipse
	 * @returns {void}
	 */
	#setPoints(center, radius = { a: 100, b: 100 }) {
		// const points = [];
		const { x: cx, y: cy } = center;
		const { a, b } = radius;

		for (let wordIndex = 0; wordIndex < this.words.length; wordIndex++) {
			// Angle for the current point (in radians)
			// Adding 1 to offset the starting position, so words are not clustered
			const t = (2 * Math.PI * wordIndex) / this.words.length + 1;

			// Parametric equations for the ellipse with added randomness
			let x = Math.floor(
				cx +
					a * Math.cos(t) +
					Math.random() * WordCloud.RANDOMNESS_RANGE -
					WordCloud.RANDOMNESS_OFFSET,
			);
			let y = Math.floor(
				cy +
					b * Math.sin(t) +
					Math.random() * WordCloud.RANDOMNESS_RANGE -
					WordCloud.RANDOMNESS_OFFSET,
			);

			this.words[wordIndex].setPosition(x, y);
			this.words[wordIndex].setStartPosition(x, y);

			// Iteratively resolve collisions until no more overlaps
			let maxIterations = 20;
			let iteration = 0;
			let hadCollision;

			do {
				hadCollision = false;

				// Check against all previously placed words
				for (let j = 0; j < wordIndex; j++) {
					if (
						this.#checkOverlap(this.words[wordIndex], this.words[j])
					) {
						hadCollision = true;
					}
				}

				// Check against logo
				if (this.#checkOverlap(this.words[wordIndex], this.logoArea)) {
					hadCollision = true;
				}

				this.#checkBounds(this.words[wordIndex]);
				iteration++;
			} while (hadCollision && iteration < maxIterations);
		}
	}

	/**
	 * Checks if two objects overlap and pushes them apart if they do.
	 * @param {Object} a - the first object with position and size this object will be moved
	 * @param {Object} b - the second object with position and size
	 * @returns {boolean} - whether any object was moved
	 */
	#checkOverlap(a, b) {
		// Calculate centers
		const centerAX = a.position.x;
		const centerAY = a.position.y;
		const centerBX = b.position.x;
		const centerBY = b.position.y;

		// Calculate distance between centers
		const dx = centerAX - centerBX;
		const dy = centerAY - centerBY;

		// Calculate overlap on x and y axes (including padding)
		const overlapX =
			(a.size.width + b.size.width) / 2 +
			WordCloud.BASIC_PADDING -
			Math.abs(dx);

		const overlapY =
			(a.size.height + b.size.height) / 2 +
			WordCloud.BASIC_PADDING -
			Math.abs(dy);

		// If there's an overlap, push object a along the axis with minimum overlap
		if (overlapX > 0 && overlapY > 0) {
			// Push along the axis with smaller overlap (minimum separation distance)
			if (overlapX < overlapY) {
				// Push horizontally
				const push = overlapX * Math.sign(dx || 1);
				a.position.x += push;
			} else {
				// Push vertically
				const push = overlapY * Math.sign(dy || 1);
				a.position.y += push;
			}
			return true;
		}

		return false;
	}

	/**
	 * Checks and adjusts word position to ensure it stays within container bounds.
	 * @param {Word} word - word to check bounds for
	 */
	#checkBounds(word) {
		if (word.position.x - word.size.width / 2 < 0) {
			word.position.x = word.size.width / 2 + WordCloud.BASIC_PADDING;
		}

		if (word.position.y - word.size.height / 2 < 0) {
			word.position.y = word.size.height / 2 + WordCloud.BASIC_PADDING;
		}

		if (word.position.x + word.size.width / 2 > this.size.width) {
			word.position.x =
				this.size.width - word.size.width / 2 - WordCloud.BASIC_PADDING;
		}

		if (word.position.y + word.size.height / 2 > this.size.height) {
			word.position.y =
				this.size.height -
				word.size.height / 2 -
				WordCloud.BASIC_PADDING;
		}
	}

	/**
	 * Sets the logo area for overlap checking.
	 * @private
	 * @returns {void}
	 */
	#setLogoArea() {
		const logo = this.element.querySelector(".word-cloud__logo");
		this.logoArea = {
			position: {
				x: logo.offsetLeft,
				y: logo.offsetTop,
			},
			size: {
				width: logo.offsetWidth,
				height: logo.offsetHeight,
			},
		};
	}

	/**
	 * Registers event listener to update word positions.
	 * @private
	 * @returns {void}
	 */
	#registerEventListeners() {
		window.addEventListener(
			"resize",
			this.#debounce(this.#resizeListener, 500).bind(this),
		);
		// TODO: Implement mouse move interaction
	}

	/**
	 * Helper function to debounce another function.
	 * @private
	 * @param {Function} func - function to debounce
	 * @param {number} wait - time in ms
	 * @returns {Function} - debounced function
	 */
	#debounce(func, wait) {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => func.apply(this, args), wait);
		};
	}

	/**
	 * Handles window resize event to update word positions.
	 * @private
	 * @returns {void}
	 */
	#resizeListener() {
		this.size.width = this.element.offsetWidth;
		this.size.height = this.element.offsetHeight;
		this.#setLogoArea();
		this.center = {
			x: this.size.width / 2,
			y: this.size.height / 2,
		};
		this.#setPoints(this.center, {
			a: Math.max(this.size.width / 3, WordCloud.ELLIPSE_A),
			b: this.size.height / 4,
		});

		this.start();
	}

	/**
	 * Starts the animation of the word cloud.
	 * @returns {void}
	 */
	start() {
		this.words.forEach((word) => {
			word.element.style.opacity = "1";
			word.element.style.top = `${word.position.y}px`;
			word.element.style.left = `${word.position.x}px`;
			word.element.style.transition =
				"opacity 1s ease-out, top 1s ease-out, left 1s ease-out";
		});

		// this.animate();
	}

	/**
	 * Animates the word cloud.
	 * @returns {void}
	 */
	animate() {
		// TODO: Implement animation logic here
		requestAnimationFrame(this.animate.bind(this));
	}
}
